/**
 * Where the report panel stops being a card and becomes the screen.
 *
 * Two viewports need the same treatment and MUI's breakpoints describe only
 * one of them. A phone in portrait is narrow, so `xs` catches it; a phone in
 * landscape is 600px or wider, takes the `sm` branch, and asked for a 620px
 * card inside a ~390px-tall viewport. Height is what the two have in common,
 * so the query asks about both axes and every part of the widget answers to
 * the one query rather than to a breakpoint each.
 */
import type { Theme } from "@mui/material/styles";

/**
 * Under this many CSS pixels of viewport height a card cannot hold the tab
 * row, a readable thread and the composer at once. Production's own
 * keyboard-open report recorded 315.
 */
const SHORT_VIEWPORT_MAX_HEIGHT_PX = 500;

/**
 * The media query under which the panel fills the layout viewport.
 *
 * Returned as a complete `@media` string: that is what an `sx` key needs, and
 * `useMediaQuery` strips the prefix itself — so the layout and the scroll lock
 * cannot drift apart.
 */
export const fullScreenPanelQuery = (theme: Theme): string =>
	`${theme.breakpoints.down("sm")}, (max-height: ${SHORT_VIEWPORT_MAX_HEIGHT_PX}px)`;

/**
 * The floating bubble's own box, and the room the app owes it.
 *
 * The bubble is fixed in the bottom-right corner, so anything the page puts
 * there is behind it: a list's pagination arrows sit exactly under it, which
 * is why stepping through pages stopped working on desktop. The app reserves
 * this much at the bottom of every routed view — see `app-viewport.tsx` — so
 * the corner belongs to the bubble and to nothing else.
 */
export const SUPPORT_BUBBLE_INSET_PX = 24;

/** A `size="medium"` Fab, which is 48px square. */
export const SUPPORT_BUBBLE_SIZE_PX = 48;

/** Inset + bubble + a little air, so a control does not merely abut it. */
export const SUPPORT_BUBBLE_GUTTER_PX =
	SUPPORT_BUBBLE_INSET_PX + SUPPORT_BUBBLE_SIZE_PX + 8;
